import { ChevronRight, Coins, History, PencilLine, Trash2, Truck } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import AppShell from '../app/AppShell';
import {
  ActionIconButton,
  CustomSelect,
  FeedbackMessage,
  CustomSearchInput,
  ModalBackdrop,
  PaginationFooter,
  ToastStack,
} from '../components/common';
import useLansas from '../hooks/useLansas';
import useSavingsPerKiloSetting from '../hooks/useSavingsPerKiloSetting';
import useSeaweedTypes from '../hooks/useSeaweedTypes';
import useTareAmountFeeSetting from '../hooks/useTareAmountFeeSetting';
import useWarehouseLocations from '../hooks/useWarehouseLocations';

const masterDataTabs = [
  { id: 'lansa-name', label: 'Lansa Name' },
  { id: 'seaweed-type', label: 'Seaweed Types' },
  { id: 'warehouse-location', label: 'Warehouse Location' },
  { id: 'tare-amount-fee', label: 'Tare Amount Fee' },
  { id: 'savings-per-kilo', label: 'Savings per Kilo' },
];
const LANSA_PAGE_SIZE = 5;
const FEE_HISTORY_PAGE_SIZE = 5;

function LansaPage({ navigationItems, activePath, activeSearch = '', onNavigate }) {
  const [activeTabId, setActiveTabId] = useState(() => getMasterDataTabId(activeSearch));
  const [deletingLansa, setDeletingLansa] = useState(null);
  const [deletingSeaweedType, setDeletingSeaweedType] = useState(null);
  const [deletingWarehouseLocation, setDeletingWarehouseLocation] = useState(null);
  const [lansaSearchValue, setLansaSearchValue] = useState('');
  const [seaweedTypeSearchValue, setSeaweedTypeSearchValue] = useState('');
  const {
    lansas,
    formValues,
    formErrors,
    editingLansa,
    editValues,
    editErrors,
    isLoading,
    isSaving,
    loadError: lansaLoadError,
    submitError: lansaSubmitError,
    successMessage: lansaSuccessMessage,
    handleFormChange,
    handleSubmit,
    openEditModal,
    closeEditModal,
    handleEditChange,
    handleEditSubmit,
    handleDelete,
  } = useLansas();
  const seaweedType = useSeaweedTypes();
  const warehouseLocation = useWarehouseLocations();
  const tareAmountFeeSetting = useTareAmountFeeSetting();
  const savingsPerKiloSetting = useSavingsPerKiloSetting();
  const isLansaTabActive = activeTabId === 'lansa-name';
  const isTareAmountFeeTabActive = activeTabId === 'tare-amount-fee';
  const isSavingsPerKiloTabActive = activeTabId === 'savings-per-kilo';
  const isWarehouseLocationTabActive = activeTabId === 'warehouse-location';
  const isSeaweedTypeTabActive = activeTabId === 'seaweed-type';
  const filteredLansas = useMemo(() => {
    const normalizedSearch = lansaSearchValue.trim().toLowerCase();

    if (!normalizedSearch) {
      return lansas;
    }

    return lansas.filter((lansa) =>
      String(lansa.name || '').toLowerCase().includes(normalizedSearch)
    );
  }, [lansaSearchValue, lansas]);
  const filteredSeaweedTypes = useMemo(() => {
    const normalizedSearch = seaweedTypeSearchValue.trim().toLowerCase();

    if (!normalizedSearch) {
      return seaweedType.seaweedTypes;
    }

    return seaweedType.seaweedTypes.filter((item) =>
      String(item.name || '').toLowerCase().includes(normalizedSearch)
    );
  }, [seaweedType.seaweedTypes, seaweedTypeSearchValue]);
  const lansaSearchSuggestions = useMemo(() => buildLansaSearchSuggestions(lansas), [lansas]);
  const seaweedTypeSearchSuggestions = useMemo(
    () => buildEntitySearchSuggestions(seaweedType.seaweedTypes, 'seaweed-type', 'Seaweed Type'),
    [seaweedType.seaweedTypes]
  );
  const toastMessages = [
    isLansaTabActive && lansaLoadError
      ? { id: 'lansa-load-error', tone: 'error', message: lansaLoadError }
      : null,
    isLansaTabActive && lansaSubmitError
      ? { id: 'lansa-submit-error', tone: 'error', message: lansaSubmitError }
      : null,
    isLansaTabActive && lansaSuccessMessage
      ? { id: 'lansa-success', tone: 'success', message: lansaSuccessMessage }
      : null,
    isTareAmountFeeTabActive && tareAmountFeeSetting.loadError
      ? {
          id: 'tare-fee-load-error',
          tone: 'error',
          message: tareAmountFeeSetting.loadError,
        }
      : null,
    isTareAmountFeeTabActive && tareAmountFeeSetting.submitError
      ? {
          id: 'tare-fee-submit-error',
          tone: 'error',
          message: tareAmountFeeSetting.submitError,
        }
      : null,
    isTareAmountFeeTabActive && tareAmountFeeSetting.successMessage
      ? {
          id: 'tare-fee-success',
          tone: 'success',
          message: tareAmountFeeSetting.successMessage,
        }
      : null,
    isSavingsPerKiloTabActive && savingsPerKiloSetting.loadError
      ? {
          id: 'savings-per-kilo-load-error',
          tone: 'error',
          message: savingsPerKiloSetting.loadError,
        }
      : null,
    isSavingsPerKiloTabActive && savingsPerKiloSetting.submitError
      ? {
          id: 'savings-per-kilo-submit-error',
          tone: 'error',
          message: savingsPerKiloSetting.submitError,
        }
      : null,
    isSavingsPerKiloTabActive && savingsPerKiloSetting.successMessage
      ? {
          id: 'savings-per-kilo-success',
          tone: 'success',
          message: savingsPerKiloSetting.successMessage,
        }
      : null,
    isWarehouseLocationTabActive && warehouseLocation.loadError
      ? {
          id: 'warehouse-location-load-error',
          tone: 'error',
          message: warehouseLocation.loadError,
        }
      : null,
    isWarehouseLocationTabActive && warehouseLocation.submitError
      ? {
          id: 'warehouse-location-submit-error',
          tone: 'error',
          message: warehouseLocation.submitError,
        }
      : null,
    isWarehouseLocationTabActive && warehouseLocation.successMessage
      ? {
          id: 'warehouse-location-success',
          tone: 'success',
          message: warehouseLocation.successMessage,
        }
      : null,
    isSeaweedTypeTabActive && seaweedType.loadError
      ? {
          id: 'seaweed-type-load-error',
          tone: 'error',
          message: seaweedType.loadError,
        }
      : null,
    isSeaweedTypeTabActive && seaweedType.submitError
      ? {
          id: 'seaweed-type-submit-error',
          tone: 'error',
          message: seaweedType.submitError,
        }
      : null,
    isSeaweedTypeTabActive && seaweedType.successMessage
      ? {
          id: 'seaweed-type-success',
          tone: 'success',
          message: seaweedType.successMessage,
        }
      : null,
  ].filter(Boolean);

  useEffect(() => {
    setActiveTabId(getMasterDataTabId(activeSearch));
  }, [activeSearch]);

  function handleTabSelect(tabId) {
    setActiveTabId(tabId);
    onNavigate(`${activePath}?tab=${tabId}`);
  }

  function openDeleteModal(lansa) {
    setDeletingLansa(lansa);
  }

  function closeDeleteModal() {
    setDeletingLansa(null);
  }

  function handleLansaFilterChange(event) {
    setLansaSearchValue(event.target.value);
  }

  function handleLansaFilterReset() {
    setLansaSearchValue('');
  }

  function openSeaweedTypeDeleteModal(item) {
    setDeletingSeaweedType(item);
  }

  function closeSeaweedTypeDeleteModal() {
    setDeletingSeaweedType(null);
  }

  function handleSeaweedTypeFilterChange(event) {
    setSeaweedTypeSearchValue(event.target.value);
  }

  function handleSeaweedTypeFilterReset() {
    setSeaweedTypeSearchValue('');
  }

  async function handleDeleteConfirm() {
    if (!deletingLansa) {
      return;
    }

    const didDelete = await handleDelete(deletingLansa);

    if (didDelete) {
      closeDeleteModal();
    }
  }

  function openWarehouseLocationDeleteModal(location) {
    setDeletingWarehouseLocation(location);
  }

  function closeWarehouseLocationDeleteModal() {
    setDeletingWarehouseLocation(null);
  }

  async function handleWarehouseLocationDeleteConfirm() {
    if (!deletingWarehouseLocation) {
      return;
    }

    const didDelete = await warehouseLocation.handleDelete(deletingWarehouseLocation);

    if (didDelete) {
      closeWarehouseLocationDeleteModal();
    }
  }

  async function handleSeaweedTypeDeleteConfirm() {
    if (!deletingSeaweedType) {
      return;
    }

    const didDelete = await seaweedType.handleDelete(deletingSeaweedType);

    if (didDelete) {
      closeSeaweedTypeDeleteModal();
    }
  }

  return (
    <AppShell
      navigationItems={navigationItems}
      activePath={activePath}
      activeSearch={activeSearch}
      onNavigate={onNavigate}
    >
      <ToastStack toasts={toastMessages} />
      <section className="mt-4 px-4 sm:mt-5 sm:px-6 lg:px-[26px]">
        <div className="grid gap-4">
          <div>
            <h2 className="m-0 text-[1.18rem] font-semibold tracking-[-0.03em] text-[var(--theme-text)] sm:text-[1.32rem]">
              Master Data
            </h2>
            <p className="m-0 mt-1.5 text-[0.9rem] text-[var(--theme-text-muted)]">
              Manage reusable records used across Seaweeds transactions.
            </p>
          </div>

          <MasterDataTabs
            tabs={masterDataTabs}
            activeTabId={activeTabId}
            onSelect={handleTabSelect}
          />

          {isLansaTabActive && isLoading ? (
            <FeedbackMessage message="Loading lansa names..." />
          ) : null}
          {isTareAmountFeeTabActive && tareAmountFeeSetting.isLoading ? (
            <FeedbackMessage message="Loading tare amount fee..." />
          ) : null}
          {isSavingsPerKiloTabActive && savingsPerKiloSetting.isLoading ? (
            <FeedbackMessage message="Loading savings per kilo..." />
          ) : null}
          {isWarehouseLocationTabActive && warehouseLocation.isLoading ? (
            <FeedbackMessage message="Loading warehouse locations..." />
          ) : null}
          {isSeaweedTypeTabActive && seaweedType.isLoading ? (
            <FeedbackMessage message="Loading seaweed types..." />
          ) : null}

          {isLansaTabActive ? (
            <>
              <div className="flex max-w-xl items-end gap-2">
                <div className="min-w-0 flex-1">
                  <CustomSearchInput
                  label="Search"
                  name="search"
                  value={lansaSearchValue}
                  onChange={handleLansaFilterChange}
                  placeholder="Search lansa name"
                  suggestions={lansaSearchSuggestions}
                />
                </div>
                {lansaSearchValue ? (
                  <button
                    className="min-h-[40px] shrink-0 rounded-full border border-[var(--theme-border)] bg-white px-4 text-[0.82rem] font-semibold text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-surface-soft)]"
                    type="button"
                    onClick={handleLansaFilterReset}
                  >
                    Clear
                  </button>
                ) : null}
              </div>

              <form
                className="max-w-2xl rounded-[18px] border border-[var(--theme-border-soft)] bg-white p-3.5 sm:p-4"
                onSubmit={handleSubmit}
              >
                <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-start">
                  <FieldGroup label="Lansa Name" error={formErrors.name}>
                    <input
                      className={`min-h-[38px] rounded-[12px] border bg-[var(--theme-surface-warm)] px-3.5 text-[0.9rem] outline-none transition ${
                        formErrors.name
                          ? 'border-[#d78b7d] focus:border-[#c76552]'
                          : 'border-[var(--theme-border)] focus:border-[var(--theme-text-subtle)]'
                      }`}
                      name="name"
                      value={formValues.name}
                      onChange={handleFormChange}
                      placeholder="Enter lansa name"
                      disabled={isSaving}
                    />
                  </FieldGroup>
                  <button
                    className="inline-flex min-h-[38px] items-center justify-center rounded-full bg-[var(--theme-accent)] px-5 text-[0.88rem] font-semibold text-white transition hover:bg-[var(--theme-accent-strong)] disabled:cursor-not-allowed disabled:bg-[var(--theme-accent-soft)] disabled:text-[var(--theme-accent-strong)] md:mt-[26px]"
                    type="submit"
                    disabled={isSaving}
                  >
                    {isSaving ? 'Saving...' : 'Add Lansa Name'}
                  </button>
                </div>
              </form>

              <LansaTable
                lansas={filteredLansas}
                isSaving={isSaving}
                onEdit={openEditModal}
                onDelete={openDeleteModal}
              />
            </>
          ) : null}

          {isTareAmountFeeTabActive ? (
            <TareAmountFeePanel setting={tareAmountFeeSetting} />
          ) : null}

          {isSavingsPerKiloTabActive ? (
            <SavingsPerKiloPanel setting={savingsPerKiloSetting} />
          ) : null}

          {isWarehouseLocationTabActive ? (
            <>
              <form
                className="max-w-2xl rounded-[18px] border border-[var(--theme-border-soft)] bg-white p-3.5 sm:p-4"
                onSubmit={warehouseLocation.handleSubmit}
              >
                <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] md:items-start">
                  <FieldGroup
                    label="Warehouse Location"
                    error={warehouseLocation.formErrors.name}
                  >
                    <input
                      className={`min-h-[38px] rounded-[12px] border bg-[var(--theme-surface-warm)] px-3.5 text-[0.9rem] outline-none transition ${
                        warehouseLocation.formErrors.name
                          ? 'border-[#d78b7d] focus:border-[#c76552]'
                          : 'border-[var(--theme-border)] focus:border-[var(--theme-text-subtle)]'
                      }`}
                      name="name"
                      value={warehouseLocation.formValues.name}
                      onChange={warehouseLocation.handleFormChange}
                      placeholder="Enter warehouse location"
                      disabled={warehouseLocation.isSaving}
                    />
                  </FieldGroup>
                  <CustomSelect
                    label="Branch"
                    name="branchId"
                    value={warehouseLocation.formValues.branchId}
                    error={warehouseLocation.formErrors.branchId}
                    onChange={warehouseLocation.handleFormChange}
                    options={warehouseLocation.branchOptions}
                    placeholder="Select branch"
                    disabled={!warehouseLocation.branchOptions.length || warehouseLocation.isSaving}
                  />
                  <button
                    className="inline-flex min-h-[38px] items-center justify-center rounded-full bg-[var(--theme-accent)] px-5 text-[0.88rem] font-semibold text-white transition hover:bg-[var(--theme-accent-strong)] disabled:cursor-not-allowed disabled:bg-[var(--theme-accent-soft)] disabled:text-[var(--theme-accent-strong)] md:mt-[26px]"
                    type="submit"
                    disabled={warehouseLocation.isSaving}
                  >
                    {warehouseLocation.isSaving ? 'Saving...' : 'Add Warehouse Location'}
                  </button>
                </div>
              </form>

              <LansaTable
                lansas={warehouseLocation.warehouseLocations}
                isSaving={warehouseLocation.isSaving}
                entityLabel="Warehouse Location"
                showBranchColumn
                emptyMessage="No warehouse locations found"
                onEdit={warehouseLocation.openEditModal}
                onDelete={openWarehouseLocationDeleteModal}
              />
            </>
          ) : null}

          {isSeaweedTypeTabActive ? (
            <>
              <div className="flex max-w-xl items-end gap-2">
                <div className="min-w-0 flex-1">
                  <CustomSearchInput
                    label="Search"
                    name="search"
                    value={seaweedTypeSearchValue}
                    onChange={handleSeaweedTypeFilterChange}
                    placeholder="Search seaweed type"
                    suggestions={seaweedTypeSearchSuggestions}
                  />
                </div>
                {seaweedTypeSearchValue ? (
                  <button
                    className="min-h-[40px] shrink-0 rounded-full border border-[var(--theme-border)] bg-white px-4 text-[0.82rem] font-semibold text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-surface-soft)]"
                    type="button"
                    onClick={handleSeaweedTypeFilterReset}
                  >
                    Clear
                  </button>
                ) : null}
              </div>

              <form
                className="max-w-2xl rounded-[18px] border border-[var(--theme-border-soft)] bg-white p-3.5 sm:p-4"
                onSubmit={seaweedType.handleSubmit}
              >
                <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] md:items-start">
                  <FieldGroup label="Seaweed Type" error={seaweedType.formErrors.name}>
                    <input
                      className={`min-h-[38px] rounded-[12px] border bg-[var(--theme-surface-warm)] px-3.5 text-[0.9rem] outline-none transition ${
                        seaweedType.formErrors.name
                          ? 'border-[#d78b7d] focus:border-[#c76552]'
                          : 'border-[var(--theme-border)] focus:border-[var(--theme-text-subtle)]'
                      }`}
                      name="name"
                      value={seaweedType.formValues.name}
                      onChange={seaweedType.handleFormChange}
                      placeholder="Enter seaweed type"
                      disabled={seaweedType.isSaving}
                    />
                  </FieldGroup>
                  <FieldGroup
                    label="Description (Optional)"
                    error={seaweedType.formErrors.description}
                  >
                    <input
                      className={`min-h-[38px] rounded-[12px] border bg-[var(--theme-surface-warm)] px-3.5 text-[0.9rem] outline-none transition ${
                        seaweedType.formErrors.description
                          ? 'border-[#d78b7d] focus:border-[#c76552]'
                          : 'border-[var(--theme-border)] focus:border-[var(--theme-text-subtle)]'
                      }`}
                      name="description"
                      value={seaweedType.formValues.description}
                      onChange={seaweedType.handleFormChange}
                      placeholder="Enter description"
                      disabled={seaweedType.isSaving}
                    />
                  </FieldGroup>
                  <button
                    className="inline-flex min-h-[38px] items-center justify-center rounded-full bg-[var(--theme-accent)] px-5 text-[0.88rem] font-semibold text-white transition hover:bg-[var(--theme-accent-strong)] disabled:cursor-not-allowed disabled:bg-[var(--theme-accent-soft)] disabled:text-[var(--theme-accent-strong)] md:mt-[26px]"
                    type="submit"
                    disabled={seaweedType.isSaving}
                  >
                    {seaweedType.isSaving ? 'Saving...' : 'Add Seaweed Type'}
                  </button>
                </div>
              </form>

              <LansaTable
                lansas={filteredSeaweedTypes}
                isSaving={seaweedType.isSaving}
                entityLabel="Seaweed Type"
                emptyMessage="No seaweed types found"
                showDescriptionColumn
                onEdit={seaweedType.openEditModal}
                onDelete={openSeaweedTypeDeleteModal}
              />
            </>
          ) : null}
        </div>
      </section>

      {editingLansa ? (
        <LansaEditModal
          values={editValues}
          errors={editErrors}
          isSaving={isSaving}
          title="Edit Lansa Name"
          fieldLabel="Lansa Name"
          onChange={handleEditChange}
          onClose={closeEditModal}
          onSubmit={handleEditSubmit}
        />
      ) : null}

      {warehouseLocation.editingWarehouseLocation ? (
        <LansaEditModal
          values={warehouseLocation.editValues}
          errors={warehouseLocation.editErrors}
          isSaving={warehouseLocation.isSaving}
          title="Edit Warehouse Location"
          fieldLabel="Warehouse Location"
          branchOptions={warehouseLocation.branchOptions}
          showBranchField
          onChange={warehouseLocation.handleEditChange}
          onClose={warehouseLocation.closeEditModal}
          onSubmit={warehouseLocation.handleEditSubmit}
        />
      ) : null}

      {seaweedType.editingSeaweedType ? (
        <LansaEditModal
          values={seaweedType.editValues}
          errors={seaweedType.editErrors}
          isSaving={seaweedType.isSaving}
          title="Edit Seaweed Type"
          fieldLabel="Seaweed Type"
          showDescriptionField
          descriptionLabel="Description (Optional)"
          onChange={seaweedType.handleEditChange}
          onClose={seaweedType.closeEditModal}
          onSubmit={seaweedType.handleEditSubmit}
        />
      ) : null}

      {deletingLansa ? (
        <LansaDeleteModal
          lansa={deletingLansa}
          isSaving={isSaving}
          title="Delete Lansa Name"
          itemLabel="Lansa Name"
          onClose={closeDeleteModal}
          onConfirm={handleDeleteConfirm}
        />
      ) : null}

      {deletingWarehouseLocation ? (
        <LansaDeleteModal
          lansa={deletingWarehouseLocation}
          isSaving={warehouseLocation.isSaving}
          title="Delete Warehouse Location"
          itemLabel="Warehouse Location"
          onClose={closeWarehouseLocationDeleteModal}
          onConfirm={handleWarehouseLocationDeleteConfirm}
        />
      ) : null}

      {deletingSeaweedType ? (
        <LansaDeleteModal
          lansa={deletingSeaweedType}
          isSaving={seaweedType.isSaving}
          title="Delete Seaweed Type"
          itemLabel="Seaweed Type"
          onClose={closeSeaweedTypeDeleteModal}
          onConfirm={handleSeaweedTypeDeleteConfirm}
        />
      ) : null}
    </AppShell>
  );
}

function getMasterDataTabId(search) {
  const requestedTabId = new URLSearchParams(search).get('tab');

  if (masterDataTabs.some((tab) => tab.id === requestedTabId)) {
    return requestedTabId;
  }

  return 'lansa-name';
}

function buildLansaSearchSuggestions(lansas) {
  return buildEntitySearchSuggestions(lansas, 'lansa-name', 'Lansa Name');
}

function buildEntitySearchSuggestions(items, group, detail) {
  return items.map((item) => ({
    group,
    value: item.name,
    label: item.name,
    detail,
  }));
}

function MasterDataTabs({ tabs, activeTabId, onSelect }) {
  const scrollRef = useRef(null);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    const scrollElement = scrollRef.current;

    if (!scrollElement) {
      return undefined;
    }

    function updateScrollHint() {
      const remainingScroll =
        scrollElement.scrollWidth - scrollElement.clientWidth - scrollElement.scrollLeft;

      setCanScrollRight(remainingScroll > 2);
    }

    updateScrollHint();
    scrollElement.addEventListener('scroll', updateScrollHint, { passive: true });
    window.addEventListener('resize', updateScrollHint);

    return () => {
      scrollElement.removeEventListener('scroll', updateScrollHint);
      window.removeEventListener('resize', updateScrollHint);
    };
  }, [tabs.length]);

  return (
    <div className="relative min-w-0 max-w-full overflow-hidden sm:w-fit">
      <div
        ref={scrollRef}
        className="inline-flex w-full max-w-full gap-1 overflow-x-auto rounded-[18px] border border-[var(--theme-border)] bg-[var(--theme-accent-soft)] p-1 pr-11 shadow-[inset_0_1px_0_rgba(255,255,255,0.68)] [scrollbar-width:none] [-ms-overflow-style:none] sm:w-fit sm:pr-1 [&::-webkit-scrollbar]:hidden"
      >
        {tabs.map((tab) => {
          const isActive = tab.id === activeTabId;

          return (
            <button
              key={tab.id}
              className={`inline-flex min-h-[44px] shrink-0 items-center justify-center rounded-[14px] px-5 text-[0.94rem] font-semibold outline-none transition focus-visible:ring-2 focus-visible:ring-[var(--theme-focus)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--theme-app-bg)] sm:px-6 ${
                isActive
                  ? 'bg-white text-[var(--theme-text)] shadow-[0_10px_22px_rgba(var(--theme-shadow-rgb),0.12)] ring-1 ring-[var(--theme-accent)]'
                  : 'text-[var(--theme-accent-strong)] hover:bg-white/65 hover:text-[var(--theme-text-strong)]'
              }`}
              type="button"
              aria-pressed={isActive}
              onClick={() => onSelect(tab.id)}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
      {canScrollRight ? (
        <div
          className="pointer-events-none absolute bottom-0 right-0 top-0 flex w-14 items-center justify-end rounded-r-[18px] bg-[linear-gradient(90deg,rgba(216,240,241,0),var(--theme-accent-soft)_42%,var(--theme-accent-soft)_100%)] pr-2 sm:hidden"
          aria-hidden="true"
        >
          <span className="grid h-8 w-8 place-items-center rounded-full border border-[var(--theme-border)] bg-white text-[var(--theme-accent)] shadow-[0_6px_14px_rgba(var(--theme-shadow-rgb),0.1)]">
            <ChevronRight className="h-4 w-4" />
          </span>
        </div>
      ) : null}
    </div>
  );
}

function TareAmountFeePanel({ setting }) {
  return (
    <div className="grid gap-4">
      <form
        className="max-w-2xl rounded-[18px] border border-[var(--theme-border-soft)] bg-white p-3.5 sm:p-4"
        onSubmit={setting.handleTareAmountFeeSubmit}
      >
        <div className="grid gap-3 sm:grid-cols-[auto_minmax(0,1fr)] sm:items-start">
          <div className="grid h-11 w-11 place-items-center rounded-full bg-[var(--theme-accent-soft)] text-[var(--theme-accent)]">
            <Coins className="h-5 w-5" aria-hidden="true" />
          </div>
          <div className="grid gap-3">
            <div>
              <h3 className="m-0 text-[1rem] font-semibold tracking-[-0.03em] text-[var(--theme-text)]">
                Tare Amount Fee
              </h3>
              <p className="m-0 mt-1 text-[0.85rem] leading-5 text-[var(--theme-text-muted)]">
                This amount is used as the default tare payment fee for supplier balancing.
              </p>
            </div>

            <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-start">
              <FieldGroup label="Tare Amount Fee" error={setting.formError}>
                <div className="relative">
                  <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[var(--theme-text-muted)]">
                    ₱
                  </span>
                  <input
                    className={`min-h-[38px] w-full rounded-[12px] border bg-[var(--theme-surface-warm)] py-2 pl-9 pr-3.5 text-[0.9rem] outline-none transition ${
                      setting.formError
                        ? 'border-[#d78b7d] focus:border-[#c76552]'
                        : 'border-[var(--theme-border)] focus:border-[var(--theme-text-subtle)]'
                    }`}
                    type="number"
                    value={setting.tareAmountFee}
                    onChange={setting.handleTareAmountFeeChange}
                    min="0"
                    step="0.01"
                    inputMode="decimal"
                    disabled={setting.isSaving}
                  />
                </div>
              </FieldGroup>
              <button
                className="inline-flex min-h-[38px] items-center justify-center rounded-full bg-[var(--theme-accent)] px-5 text-[0.88rem] font-semibold text-white transition hover:bg-[var(--theme-accent-strong)] disabled:cursor-not-allowed disabled:bg-[var(--theme-accent-soft)] disabled:text-[var(--theme-accent-strong)] md:mt-[26px]"
                type="submit"
                disabled={setting.isSaving}
              >
                {setting.isSaving ? 'Saving...' : 'Save Fee'}
              </button>
            </div>
          </div>
        </div>
      </form>

      <FeeHistoryPanel
        setting={setting}
        title="Tare Fee History"
        description="Recent changes to the shared tare amount fee."
        emptyMessage="No tare fee changes yet."
      />
    </div>
  );
}

function SavingsPerKiloPanel({ setting }) {
  return (
    <div className="grid gap-4">
      <form
        className="max-w-2xl rounded-[18px] border border-[var(--theme-border-soft)] bg-white p-3.5 sm:p-4"
        onSubmit={setting.handleSavingsPerKiloSubmit}
      >
        <div className="grid gap-3 sm:grid-cols-[auto_minmax(0,1fr)] sm:items-start">
          <div className="grid h-11 w-11 place-items-center rounded-full bg-[var(--theme-accent-soft)] text-[var(--theme-accent)]">
            <Coins className="h-5 w-5" aria-hidden="true" />
          </div>
          <div className="grid gap-3">
            <div>
              <h3 className="m-0 text-[1rem] font-semibold tracking-[-0.03em] text-[var(--theme-text)]">
                Savings per Kilo
              </h3>
              <p className="m-0 mt-1 text-[0.85rem] leading-5 text-[var(--theme-text-muted)]">
                This amount is used as the default supplier savings value per kilo.
              </p>
            </div>

            <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-start">
              <FieldGroup label="Savings per Kilo" error={setting.formError}>
                <div className="relative">
                  <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[var(--theme-text-muted)]">
                    ₱
                  </span>
                  <input
                    className={`min-h-[38px] w-full rounded-[12px] border bg-[var(--theme-surface-warm)] py-2 pl-9 pr-3.5 text-[0.9rem] outline-none transition ${
                      setting.formError
                        ? 'border-[#d78b7d] focus:border-[#c76552]'
                        : 'border-[var(--theme-border)] focus:border-[var(--theme-text-subtle)]'
                    }`}
                    type="number"
                    value={setting.savingsPerKilo}
                    onChange={setting.handleSavingsPerKiloChange}
                    min="0"
                    step="0.01"
                    inputMode="decimal"
                    disabled={setting.isSaving}
                  />
                </div>
              </FieldGroup>
              <button
                className="inline-flex min-h-[38px] items-center justify-center rounded-full bg-[var(--theme-accent)] px-5 text-[0.88rem] font-semibold text-white transition hover:bg-[var(--theme-accent-strong)] disabled:cursor-not-allowed disabled:bg-[var(--theme-accent-soft)] disabled:text-[var(--theme-accent-strong)] md:mt-[26px]"
                type="submit"
                disabled={setting.isSaving}
              >
                {setting.isSaving ? 'Saving...' : 'Save Fee'}
              </button>
            </div>
          </div>
        </div>
      </form>

      <FeeHistoryPanel
        setting={setting}
        title="Savings per Kilo History"
        description="Recent changes to the shared savings per kilo value."
        emptyMessage="No savings per kilo changes yet."
      />
    </div>
  );
}

function FeeHistoryPanel({
  setting,
  title,
  description,
  emptyMessage,
}) {
  const [currentPage, setCurrentPage] = useState(1);
  const totalLogs = setting.historyLogs.length;
  const totalPages = Math.max(1, Math.ceil(totalLogs / FEE_HISTORY_PAGE_SIZE));
  const normalizedPage = Math.min(currentPage, totalPages);
  const startIndex = (normalizedPage - 1) * FEE_HISTORY_PAGE_SIZE;
  const endIndex = Math.min(startIndex + FEE_HISTORY_PAGE_SIZE, totalLogs);
  const visibleLogs = setting.historyLogs.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(1);
  }, [totalLogs]);

  function goToPreviousPage() {
    setCurrentPage((page) => Math.max(1, page - 1));
  }

  function goToNextPage() {
    setCurrentPage((page) => Math.min(totalPages, page + 1));
  }

  return (
    <section className="max-w-4xl overflow-hidden rounded-[18px] border border-[var(--theme-border-soft)] bg-white">
      <div className="flex items-center gap-3 border-b border-[var(--theme-border-soft)] bg-white px-4 py-3.5">
        <div className="grid h-9 w-9 place-items-center rounded-full bg-[var(--theme-accent-soft)] text-[var(--theme-accent)]">
          <History className="h-[18px] w-[18px]" aria-hidden="true" />
        </div>
        <div>
          <h3 className="m-0 text-[0.98rem] font-semibold tracking-[-0.03em] text-[var(--theme-text)]">
            {title}
          </h3>
          <p className="m-0 mt-0.5 text-[0.82rem] text-[var(--theme-text-muted)]">
            {description}
          </p>
        </div>
      </div>

      {setting.isHistoryLoading ? (
        <div className="grid gap-2 p-4">
          <span className="h-4 w-3/4 animate-pulse rounded-full bg-[var(--theme-border-soft)]" />
          <span className="h-4 w-1/2 animate-pulse rounded-full bg-[var(--theme-accent-soft)]" />
        </div>
      ) : setting.historyLoadError ? (
        <div className="p-4">
          <FeedbackMessage tone="error" message={setting.historyLoadError} />
        </div>
      ) : setting.historyLogs.length ? (
        <>
          <div className="hidden overflow-x-auto md:block">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-[var(--theme-accent-panel)]">
                  <TableHeading>Date</TableHeading>
                  <TableHeading>Changed By</TableHeading>
                  <TableHeading>Previous Fee</TableHeading>
                  <TableHeading>New Fee</TableHeading>
                </tr>
              </thead>
              <tbody>
                {visibleLogs.map((log) => (
                  <tr key={log.id} className="border-t border-[var(--theme-border-soft)]">
                    <TableCell>{formatDateTime(log.createdAt)}</TableCell>
                    <TableCell>{log.changedByName || 'Admin'}</TableCell>
                    <TableCell>{formatCurrency(log.previousValue)}</TableCell>
                    <TableCell>{formatCurrency(log.nextValue)}</TableCell>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="grid gap-2.5 p-3 md:hidden">
            {visibleLogs.map((log) => (
              <article
                key={log.id}
                className="rounded-[15px] border border-[var(--theme-border-soft)] bg-white p-3.5"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <strong className="block text-[0.92rem] text-[var(--theme-text)]">
                      {formatCurrency(log.previousValue)} to {formatCurrency(log.nextValue)}
                    </strong>
                    <span className="mt-1 block text-[0.8rem] text-[var(--theme-text-muted)]">
                      {formatDateTime(log.createdAt)}
                    </span>
                  </div>
                  <span className="text-right text-[0.82rem] font-medium text-[var(--theme-text-subtle)]">
                    {log.changedByName || 'Admin'}
                  </span>
                </div>
              </article>
            ))}
          </div>

          <PaginationFooter
            currentPage={normalizedPage}
            endItem={endIndex}
            isNextDisabled={normalizedPage >= totalPages}
            isPreviousDisabled={normalizedPage <= 1}
            onNext={goToNextPage}
            onPrevious={goToPreviousPage}
            startItem={startIndex + 1}
            totalItems={totalLogs}
            totalPages={totalPages}
          />
        </>
      ) : (
        <div className="px-4 py-8 text-center text-[0.9rem] text-[var(--theme-text-muted)]">
          {emptyMessage}
        </div>
      )}
    </section>
  );
}

function LansaTable({
  lansas,
  isSaving,
  entityLabel = 'Lansa Name',
  emptyMessage = 'No lansa names found',
  showDescriptionColumn = false,
  showBranchColumn = false,
  onEdit,
  onDelete,
}) {
  const [currentPage, setCurrentPage] = useState(1);
  const totalLansas = lansas.length;
  const totalPages = Math.max(1, Math.ceil(totalLansas / LANSA_PAGE_SIZE));
  const normalizedPage = Math.min(currentPage, totalPages);
  const startIndex = (normalizedPage - 1) * LANSA_PAGE_SIZE;
  const endIndex = Math.min(startIndex + LANSA_PAGE_SIZE, totalLansas);
  const visibleLansas = lansas.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(1);
  }, [totalLansas]);

  function goToPreviousPage() {
    setCurrentPage((page) => Math.max(1, page - 1));
  }

  function goToNextPage() {
    setCurrentPage((page) => Math.min(totalPages, page + 1));
  }

  return (
    <div className="max-w-4xl overflow-hidden rounded-[18px] border border-[var(--theme-border-soft)] bg-white">
      <div className="hidden overflow-x-auto md:block">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-[var(--theme-accent-panel)]">
              <TableHeading>{entityLabel}</TableHeading>
              {showBranchColumn ? <TableHeading>Branch</TableHeading> : null}
              {showDescriptionColumn ? <TableHeading>Description</TableHeading> : null}
              <TableHeading>Created</TableHeading>
              <TableHeading>Actions</TableHeading>
            </tr>
          </thead>
          <tbody>
            {lansas.length ? (
              visibleLansas.map((lansa) => (
                <tr key={lansa.id} className="border-t border-[var(--theme-border-soft)]">
                  <TableCell>{lansa.name}</TableCell>
                  {showBranchColumn ? (
                    <TableCell>{lansa.branchName || '-'}</TableCell>
                  ) : null}
                  {showDescriptionColumn ? (
                    <TableCell>{lansa.description || '-'}</TableCell>
                  ) : null}
                  <TableCell>{formatDate(lansa.createdAt)}</TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <CompactActionButton label={`Edit ${lansa.name}`} onClick={() => onEdit(lansa)}>
                        <PencilLine className="h-3.5 w-3.5" aria-hidden="true" />
                      </CompactActionButton>
                      <CompactActionButton
                        label={`Delete ${lansa.name}`}
                        onClick={() => onDelete(lansa)}
                        disabled={isSaving}
                        tone="danger"
                      >
                        <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                      </CompactActionButton>
                    </div>
                  </TableCell>
                </tr>
              ))
            ) : (
              <tr className="border-t border-[var(--theme-border-soft)]">
                <td
                  className="px-5 py-10"
                  colSpan={3 + (showDescriptionColumn ? 1 : 0) + (showBranchColumn ? 1 : 0)}
                >
                  <EmptyState message={emptyMessage} />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="grid gap-2.5 p-3 md:hidden">
        {lansas.length ? (
          visibleLansas.map((lansa) => (
            <article
              key={lansa.id}
              className="rounded-[15px] border border-[var(--theme-border-soft)] bg-white p-3.5"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <strong className="block text-[0.94rem] text-[var(--theme-text)]">{lansa.name}</strong>
                  {showBranchColumn ? (
                    <span className="mt-0.5 block text-[0.82rem] text-[var(--theme-text-muted)]">
                      Branch: {lansa.branchName || '-'}
                    </span>
                  ) : null}
                  {showDescriptionColumn ? (
                    <span className="mt-0.5 block text-[0.82rem] text-[var(--theme-text-muted)]">
                      {lansa.description || '-'}
                    </span>
                  ) : null}
                  <span className="mt-0.5 block text-[0.82rem] text-[var(--theme-text-muted)]">
                    {formatDate(lansa.createdAt)}
                  </span>
                </div>
                <div className="flex gap-2">
                  <ActionIconButton label={`Edit ${lansa.name}`} onClick={() => onEdit(lansa)}>
                    <PencilLine className="h-4 w-4" aria-hidden="true" />
                  </ActionIconButton>
                  <ActionIconButton
                    label={`Delete ${lansa.name}`}
                    onClick={() => onDelete(lansa)}
                    disabled={isSaving}
                    tone="danger"
                  >
                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                  </ActionIconButton>
                </div>
              </div>
            </article>
          ))
        ) : (
          <div className="rounded-[15px] border border-dashed border-[var(--theme-border)] bg-white px-4 py-9">
            <EmptyState message={emptyMessage} />
          </div>
        )}
      </div>

      {lansas.length ? (
        <div className="[&>div]:px-4 [&>div]:py-2 [&_button]:px-3.5 [&_button]:py-1.5">
          <PaginationFooter
            currentPage={normalizedPage}
            endItem={endIndex}
            isNextDisabled={normalizedPage >= totalPages}
            isPreviousDisabled={normalizedPage <= 1}
            onNext={goToNextPage}
            onPrevious={goToPreviousPage}
            startItem={startIndex + 1}
            totalItems={totalLansas}
            totalPages={totalPages}
          />
        </div>
      ) : null}
    </div>
  );
}

function LansaEditModal({
  values,
  errors,
  isSaving,
  title = 'Edit Lansa Name',
  fieldLabel = 'Lansa Name',
  branchOptions = [],
  showBranchField = false,
  showDescriptionField = false,
  descriptionLabel = 'Description',
  onChange,
  onClose,
  onSubmit,
}) {
  return (
    <ModalBackdrop backdropClassName="bg-[rgba(33,27,22,0.16)]">
      <form
        className="w-full max-w-sm rounded-[20px] border border-[var(--theme-border-soft)] bg-white p-5 shadow-[0_22px_60px_rgba(var(--theme-shadow-rgb),0.14)]"
        onSubmit={onSubmit}
      >
        <h3 className="m-0 text-[1.05rem] font-semibold tracking-[-0.03em] text-[var(--theme-text)]">
          {title}
        </h3>
        <div className="mt-4">
          <FieldGroup label={fieldLabel} error={errors.name}>
            <input
              className={`min-h-[38px] rounded-[12px] border bg-[var(--theme-surface-warm)] px-3.5 text-[0.9rem] outline-none transition ${
                errors.name
                  ? 'border-[#d78b7d] focus:border-[#c76552]'
                  : 'border-[var(--theme-border)] focus:border-[var(--theme-text-subtle)]'
              }`}
              name="name"
              value={values.name}
              onChange={onChange}
              disabled={isSaving}
            />
          </FieldGroup>
          {showBranchField ? (
            <div className="mt-3">
              <CustomSelect
                label="Branch"
                name="branchId"
                value={values.branchId || ''}
                error={errors.branchId}
                onChange={onChange}
                options={branchOptions}
                placeholder="Select branch"
                disabled={!branchOptions.length || isSaving}
              />
            </div>
          ) : null}
          {showDescriptionField ? (
            <div className="mt-3">
              <FieldGroup label={descriptionLabel} error={errors.description}>
                <input
                  className={`min-h-[38px] rounded-[12px] border bg-[var(--theme-surface-warm)] px-3.5 text-[0.9rem] outline-none transition ${
                    errors.description
                      ? 'border-[#d78b7d] focus:border-[#c76552]'
                      : 'border-[var(--theme-border)] focus:border-[var(--theme-text-subtle)]'
                  }`}
                  name="description"
                  value={values.description || ''}
                  onChange={onChange}
                  disabled={isSaving}
                  placeholder="Enter description"
                />
              </FieldGroup>
            </div>
          ) : null}
        </div>
        <div className="mt-5 grid gap-2.5 sm:grid-cols-2">
          <button
            className="rounded-full border border-[var(--theme-border)] bg-white px-4 py-2.5 text-[0.88rem] font-semibold text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-surface-soft)]"
            type="button"
            onClick={onClose}
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            className="rounded-full bg-[var(--theme-accent)] px-4 py-2.5 text-[0.88rem] font-semibold text-white transition hover:bg-[var(--theme-accent-strong)] disabled:cursor-not-allowed disabled:bg-[var(--theme-accent-soft)] disabled:text-[var(--theme-accent-strong)]"
            type="submit"
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </form>
    </ModalBackdrop>
  );
}

function CompactActionButton({
  children,
  label,
  onClick,
  disabled = false,
  tone = 'neutral',
}) {
  const toneClassName =
    tone === 'danger'
      ? 'border-[#f0ddd8] text-[#9a5649] hover:border-[#e1c2ba] hover:bg-[#fff3f0]'
      : 'border-[#e3d9cf] text-[#73675d] hover:border-[#d3c5b8] hover:bg-[var(--theme-surface-soft)]';

  return (
    <button
      className={`inline-flex h-8 w-8 items-center justify-center rounded-full border bg-white transition disabled:cursor-not-allowed disabled:opacity-60 ${toneClassName}`}
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
    >
      {children}
    </button>
  );
}

function LansaDeleteModal({
  lansa,
  isSaving,
  title = 'Delete Lansa Name',
  itemLabel = 'Lansa Name',
  onClose,
  onConfirm,
}) {
  return (
    <ModalBackdrop backdropClassName="bg-[rgba(33,27,22,0.16)]">
      <div className="w-full max-w-sm rounded-[20px] border border-[var(--theme-border-soft)] bg-white p-5 shadow-[0_22px_60px_rgba(var(--theme-shadow-rgb),0.14)]">
        <h3 className="m-0 text-[1.05rem] font-semibold tracking-[-0.03em] text-[var(--theme-text)]">
          {title}
        </h3>
        <p className="m-0 mt-3 text-[0.9rem] leading-6 text-[var(--theme-text-subtle)]">
          Are you sure you want to delete this {itemLabel.toLowerCase()}{' '}
          <strong className="text-[var(--theme-text)]">{lansa.name}</strong>?
        </p>
        <div className="mt-5 grid gap-2.5 sm:grid-cols-2">
          <button
            className="rounded-full border border-[var(--theme-border)] bg-white px-4 py-2.5 text-[0.88rem] font-semibold text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-surface-soft)]"
            type="button"
            onClick={onClose}
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            className="rounded-full bg-[#bb5745] px-4 py-2.5 text-[0.88rem] font-semibold text-white transition hover:bg-[#a54b3b] disabled:cursor-not-allowed disabled:border disabled:border-[var(--theme-border)] disabled:bg-[var(--theme-accent-soft)] disabled:text-[var(--theme-accent-strong)]"
            type="button"
            onClick={onConfirm}
            disabled={isSaving}
          >
            {isSaving ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </div>
    </ModalBackdrop>
  );
}

function FieldGroup({ label, error, children }) {
  return (
    <label className="grid gap-1.5">
      <span className="text-[0.82rem] font-medium text-[var(--theme-text-subtle)]">{label}</span>
      {children}
      {error ? <span className="text-[0.78rem] text-[#bb5745]">{error}</span> : null}
    </label>
  );
}

function TableHeading({ children }) {
  return (
    <th className="px-4 py-2.5 text-left text-[0.74rem] font-semibold uppercase tracking-[0.05em] text-[var(--theme-accent-panel-text)]">
      {children}
    </th>
  );
}

function TableCell({ children }) {
  return <td className="px-4 py-2.5 align-middle text-[0.88rem] text-[#222222]">{children}</td>;
}

function EmptyState({ message = 'No lansa names found' }) {
  return (
    <div className="flex flex-col items-center justify-center text-center text-[var(--theme-text-muted)]">
      <div className="grid h-11 w-11 place-items-center rounded-full bg-[var(--theme-accent-soft)] text-[var(--theme-text-muted)]">
        <Truck className="h-5 w-5" aria-hidden="true" />
      </div>
      <strong className="mt-3 text-[0.92rem] font-medium text-[var(--theme-text-strong)]">
        {message}
      </strong>
    </div>
  );
}

function formatDate(value) {
  if (!value) {
    return '-';
  }

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(value));
}

function formatDateTime(value) {
  if (!value) {
    return '-';
  }

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(value));
}

function formatCurrency(value) {
  if (value === null || typeof value === 'undefined') {
    return '-';
  }

  return Number(value).toLocaleString('en-PH', {
    style: 'currency',
    currency: 'PHP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export default LansaPage;
